export default class CurrencyUtils {
	public static getDuration(days: number | null, isAutoRenew = false) {
		if (days === null) {
			return '';
		}
		if (!isAutoRenew) {
			return `for ${days} days access`;
		}
		if (days === 7) {
			return 'week';
		} else if (days >= 28 && days <= 31) {
			return 'month';
		} else if (days === 365 || days === 366) {
			return 'year';
		} else {
			return `/ ${days} days`;
		}
	}

	/**
	 * Helper function to get the locale for a given currency
	 */
	public static getLocale(currency: string) {
		switch (currency) {
			case 'AUD':
				return 'en-AU';
			case 'USD':
				return 'en-US';
			case 'EUR':
				return 'en-EU';
			case 'GBP':
				return 'en-GB';
			case 'NZD':
				return 'en-NZ';
			case 'CAD':
				return 'en-CA';
			default:
				return 'en-AU';
		}
	}

	public static format(cents: number, currency = 'AUD', locale = 'en-AU') {
		const currentLocale = this.getLocale(currency) ?? locale;

		return new Intl.NumberFormat(currentLocale, {
			style: 'currency',
			currency,
		}).format(cents / 100);
	}

	/**
	 * Calculate cost per day from price in cents and duration in days
	 */
	public static costPerDay(priceInCents: number, durationInDays: number | null | undefined): number | null {
		if (priceInCents <= 0 || !durationInDays || durationInDays <= 0) {
			return null;
		}
		return priceInCents / durationInDays;
	}

	/**
	 * Format monthly equivalent price from total price and duration
	 */
	public static formatPerMonth(
		priceInCents: number,
		durationInDays: number | null | undefined,
		currency = 'AUD',
	): string {
		const locale = this.getLocale(currency);

		if (priceInCents <= 0 || !durationInDays || durationInDays <= 0) {
			return this.format(0, currency, locale);
		}
		// Calculate monthly price: (price / days) * 30.44 (average days per month)
		const monthlyPrice = (priceInCents / durationInDays) * 30.44;

		return this.format(Math.round(monthlyPrice), currency, locale);
	}

	/**
	 * Get short duration label (e.g., "2yr", "1mo", "1wk")
	 */
	public static durationLabelShort(days?: number | null): string {
		if (days == null || days <= 0) {
			return '';
		}
		if (days === 7) {
			return '1wk';
		}
		if (days >= 28 && days <= 31) {
			return '1mo';
		}

		// Handle months (32-364 days)
		if (days < 365) {
			const months = Math.round(days / 30);
			return months > 0 ? `${months}mo` : '1mo';
		}

		// Handle years (365+ days)
		const years = Math.round(days / 365);
		return `${years}yr`;
	}

	/**
	 * Get duration label in sentence format (e.g., "2 years", "5 years", "1 month")
	 */
	public static durationLabelSentence(days: number | null | undefined): string {
		if (!days) {
			return '';
		}
		if (days === 7) {
			return '1 week';
		} else if (days >= 28 && days <= 31) {
			return '1 month';
		} else if (days >= 60 && days <= 90) {
			const months = Math.round(days / 30);
			return `${months} ${months === 1 ? 'month' : 'months'}`;
		} else if (days >= 365 && days <= 366) {
			return '1 year';
		} else if (days >= 730 && days <= 732) {
			return '2 years';
		} else if (days >= 1095 && days <= 1098) {
			return '3 years';
		} else if (days >= 1460 && days <= 1465) {
			return '4 years';
		} else if (days >= 1825 && days <= 1830) {
			return '5 years';
		} else {
			// For other durations, calculate years or months
			const years = Math.round(days / 365);
			if (years > 0) {
				return `${years} ${years === 1 ? 'year' : 'years'}`;
			}
			const months = Math.round(days / 30);
			return `${months} ${months === 1 ? 'month' : 'months'}`;
		}
	}
}
